import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../constants/helpRouteMap";
import {
  BlocklistEntry,
  createBlocklistEntry,
  deleteBlocklistEntry,
  getBlocklist,
} from "../../helpers/audience_helper";

const PER_PAGE = 25;
const SEARCH_DEBOUNCE_MS = 350;

type FormValues = {
  type: "email" | "domain";
  value: string;
  reason: string;
};

/**
 * Lista de bloqueio do tenant (LAC-037 · DOC 03 § 4.9 / 09).
 * Por quê: Carla impede e-mails/domínios de voltarem à base sem Postman.
 */
const BlocklistPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";

  document.title = t("blocklist.documentTitle");

  const [items, setItems] = useState<BlocklistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"" | "email" | "domain">("");

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<BlocklistEntry | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBlocklist({
        page,
        per_page: PER_PAGE,
        search: appliedSearch || undefined,
        type: typeFilter || undefined,
      });
      if (!res?.success) {
        setError(t("blocklist.error.load"));
        setItems([]);
        return;
      }
      setItems(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
    } catch (e: any) {
      setError(e?.message || t("blocklist.error.load"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, typeFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      setAppliedSearch((prev) => {
        if (prev === next) {
          return prev;
        }
        setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Yup: regra do value muda conforme type (e-mail vs domínio sem @).
  const validationSchema = useMemo(
    () =>
      Yup.object({
        type: Yup.mixed<"email" | "domain">()
          .oneOf(["email", "domain"])
          .required(t("blocklist.form.typeRequired")),
        value: Yup.string()
          .trim()
          .required(t("blocklist.form.valueRequired"))
          .when("type", {
            is: "email",
            // E-mail completo (local@domínio).
            then: (schema) =>
              schema.email(t("blocklist.form.valueInvalidEmail")),
            // Domínio: letras/números/hífen/ponto — sem protocolo e sem @.
            otherwise: (schema) =>
              schema.matches(
                /^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/i,
                t("blocklist.form.valueInvalidDomain")
              ),
          }),
        reason: Yup.string().trim().max(255),
      }),
    [t]
  );

  const formik = useFormik<FormValues>({
    initialValues: { type: "email", value: "", reason: "" },
    validationSchema,
    onSubmit: async (values, helpers) => {
      setSaving(true);
      try {
        const res = await createBlocklistEntry({
          type: values.type,
          value: values.value.trim(),
          reason: values.reason.trim() || null,
        });
        if (!res?.success) {
          helpers.setFieldError("value", t("blocklist.error.save"));
          return;
        }
        toast.success(res.message || t("blocklist.toast.created"), {
          autoClose: 2500,
        });
        setModalOpen(false);
        helpers.resetForm();
        setPage(1);
        await load();
      } catch (err: any) {
        const details = err?.details || err?.response?.data?.error?.details;
        if (details?.value?.[0]) {
          helpers.setFieldError("value", details.value[0]);
        } else if (details?.type?.[0]) {
          helpers.setFieldError("type", details.type[0]);
        } else {
          helpers.setFieldError(
            "value",
            err?.message || t("blocklist.error.save")
          );
        }
      } finally {
        setSaving(false);
      }
    },
  });

  const openCreate = () => {
    formik.resetForm({
      values: { type: "email", value: "", reason: "" },
    });
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeletingBusy(true);
    try {
      const res = await deleteBlocklistEntry(deleting.id);
      toast.success(res?.message || t("blocklist.toast.deleted"), {
        autoClose: 2500,
      });
      setDeleting(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("blocklist.error.delete"));
      setDeleting(null);
    } finally {
      setDeletingBusy(false);
    }
  };

  const formatWhen = (iso?: string) => {
    if (!iso) {
      return "—";
    }
    try {
      return new Date(iso).toLocaleString(locale);
    } catch {
      return iso;
    }
  };

  const typeBadge = (type: string) => (
    <Badge
      color={type === "domain" ? "warning" : "primary"}
      className="text-uppercase"
      pill
    >
      {t(`blocklist.type.${type}`)}
    </Badge>
  );

  const displayValue = (row: BlocklistEntry) =>
    row.type === "domain" ? `@${row.value}` : row.value;

  const domainPreview =
    formik.values.type === "domain" && formik.values.value.trim()
      ? formik.values.value
          .trim()
          .toLowerCase()
          .replace(/^@/, "")
          .split("@")
          .pop() || ""
      : "";

  const hasFilters = appliedSearch !== "" || typeFilter !== "";
  const helpHref = helpUrlForRoute("/contacts/blocklist", locale);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("blocklist.title")}
          actions={
            <PageHelpLink href={helpHref} label={t("blocklist.help")} />
          }
        />
        <ToastContainer />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <DismissiblePageTip tipId="blocklist">
          {t("blocklist.tip")}
        </DismissiblePageTip>

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-start">
                  <div>
                    <h5 className="card-title mb-1">{t("blocklist.title")}</h5>
                    <p className="text-muted mb-0">{t("blocklist.subtitle")}</p>
                  </div>
                  <Button color="primary" onClick={openCreate}>
                    {t("blocklist.add")}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Row className="g-2 mb-3">
                  <Col md={5}>
                    <Input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t("blocklist.search.placeholder")}
                      aria-label={t("blocklist.search.placeholder")}
                    />
                  </Col>
                  <Col md={3}>
                    <Input
                      type="select"
                      value={typeFilter}
                      onChange={(e) => {
                        setTypeFilter(
                          e.target.value as "" | "email" | "domain"
                        );
                        setPage(1);
                      }}
                      aria-label={t("blocklist.filter.type")}
                    >
                      <option value="">{t("blocklist.filter.typeAll")}</option>
                      <option value="email">{t("blocklist.type.email")}</option>
                      <option value="domain">
                        {t("blocklist.type.domain")}
                      </option>
                    </Input>
                  </Col>
                  {hasFilters && (
                    <Col md="auto">
                      <Button
                        color="light"
                        onClick={() => {
                          setSearch("");
                          setAppliedSearch("");
                          setTypeFilter("");
                          setPage(1);
                        }}
                      >
                        {t("blocklist.filter.clear")}
                      </Button>
                    </Col>
                  )}
                </Row>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <div className="avatar-md mx-auto mb-3">
                      <div className="avatar-title bg-light text-primary rounded-circle fs-2">
                        <i className="ri-forbid-line" />
                      </div>
                    </div>
                    <h5>
                      {hasFilters
                        ? t("blocklist.empty.filteredTitle")
                        : t("blocklist.empty.title")}
                    </h5>
                    <p className="mb-3">
                      {hasFilters
                        ? t("blocklist.empty.filteredDescription")
                        : t("blocklist.empty.description")}
                    </p>
                    {!hasFilters && (
                      <Button color="primary" onClick={openCreate}>
                        {t("blocklist.empty.cta")}
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-muted mb-2">
                      {t("blocklist.totalCount", { count: total })}
                    </p>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("blocklist.table.value")}</th>
                            <th>{t("blocklist.table.type")}</th>
                            <th>{t("blocklist.table.reason")}</th>
                            <th>{t("blocklist.table.createdAt")}</th>
                            <th style={{ width: 90 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((row) => (
                            <tr key={row.id}>
                              <td className="fw-medium">{displayValue(row)}</td>
                              <td>{typeBadge(row.type)}</td>
                              <td className="text-muted">
                                {row.reason || "—"}
                              </td>
                              <td>{formatWhen(row.created_at)}</td>
                              <td>
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  onClick={() => setDeleting(row)}
                                  aria-label={t("blocklist.delete.button")}
                                >
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {lastPage > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <span className="text-muted">
                          {t("blocklist.pagination.label", {
                            page,
                            lastPage,
                            total,
                          })}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            {t("blocklist.pagination.prev")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page >= lastPage}
                            onClick={() =>
                              setPage((p) => Math.min(lastPage, p + 1))
                            }
                          >
                            {t("blocklist.pagination.next")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        centered
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <ModalHeader toggle={() => setModalOpen(false)}>
            {t("blocklist.add")}
          </ModalHeader>
          <ModalBody>
            <p className="text-muted small mb-3">
              {t("blocklist.form.help")}
            </p>
            <div className="mb-3">
              <Label htmlFor="blocklist-type">{t("blocklist.form.type")}</Label>
              <Input
                id="blocklist-type"
                type="select"
                name="type"
                value={formik.values.type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.type && formik.errors.type)}
              >
                <option value="email">{t("blocklist.type.email")}</option>
                <option value="domain">{t("blocklist.type.domain")}</option>
              </Input>
              {formik.touched.type && formik.errors.type && (
                <FormFeedback>{formik.errors.type}</FormFeedback>
              )}
            </div>
            <div className="mb-3">
              <Label htmlFor="blocklist-value">
                {t("blocklist.form.value")}
              </Label>
              <Input
                id="blocklist-value"
                name="value"
                value={formik.values.value}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.value && formik.errors.value)}
                placeholder={
                  formik.values.type === "email"
                    ? t("blocklist.form.valuePlaceholderEmail")
                    : t("blocklist.form.valuePlaceholderDomain")
                }
              />
              {formik.touched.value && formik.errors.value && (
                <FormFeedback>{formik.errors.value}</FormFeedback>
              )}
              {formik.values.type === "domain" && domainPreview && (
                <div className="form-text text-primary mt-1">
                  {t("blocklist.form.domainPreview", { domain: domainPreview })}
                </div>
              )}
            </div>
            <div className="mb-0">
              <Label htmlFor="blocklist-reason">
                {t("blocklist.form.reason")}
              </Label>
              <Input
                id="blocklist-reason"
                name="reason"
                value={formik.values.reason}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("blocklist.form.reasonPlaceholder")}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              color="light"
              onClick={() => setModalOpen(false)}
            >
              {t("blocklist.form.cancel")}
            </Button>
            <Button type="submit" color="primary" disabled={saving}>
              {saving ? <Spinner size="sm" /> : t("blocklist.form.save")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={!!deleting}
        toggle={() => !deletingBusy && setDeleting(null)}
        centered
      >
        <ModalHeader toggle={() => !deletingBusy && setDeleting(null)}>
          {t("blocklist.delete.title")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            {t("blocklist.delete.confirmBefore")}{" "}
            <strong className="text-danger">
              {deleting ? displayValue(deleting) : ""}
            </strong>
            {t("blocklist.delete.confirmAfter")}
          </p>
          <p className="text-muted mb-0">
            {t("blocklist.delete.consequence")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={deletingBusy}
            onClick={() => setDeleting(null)}
          >
            {t("blocklist.form.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={deletingBusy}
            onClick={confirmDelete}
          >
            {deletingBusy ? (
              <Spinner size="sm" />
            ) : (
              t("blocklist.delete.confirmButton")
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default BlocklistPage;
